import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';
import TaskDetailModal from '../components/TaskDetailModal';
import { decodeEntities } from '../utils/decodeEntities';

const STATUS_FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'not_started', label: 'Not Started' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'overdue',     label: 'Overdue' },
  { key: 'completed',   label: 'Completed' },
];

const PRIORITY_COLORS = {
  high:   { bg: '#fef2f2', text: '#dc2626' },
  medium: { bg: '#fffbeb', text: '#d97706' },
  low:    { bg: '#f0fdf4', text: '#16a34a' },
};

export default function MyTasksPage() {
  const navigate                      = useNavigate();
  const [tasks,       setTasks]       = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [detailTask,  setDetailTask]  = useState(null);
  const [filter,      setFilter]      = useState('all');
  const [priority,    setPriority]    = useState('all');
  const [search,      setSearch]      = useState('');
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState({ title: '', project_id: '', priority: 'medium', start_date: '', end_date: '', status: 'not_started' });
  const [formError,   setFormError]   = useState('');
  const [saving,      setSaving]      = useState(false);
  const [users,       setUsers]       = useState([]);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, pRes, uRes] = await Promise.allSettled([
        api.get('/wp/v2/tasks?per_page=100&orderby=date&order=desc'),
        api.get('/wp/v2/projects?per_page=100'),
        api.get('/wp/v2/users?per_page=100'),
      ]);
      if (tRes.status === 'fulfilled') setTasks(tRes.value.data || []);
      if (pRes.status === 'fulfilled') setProjects(pRes.value.data || []);
      if (uRes.status === 'fulfilled') setUsers(uRes.value.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getProjectName = (id) => {
    const p = projects.find(p => p.id === parseInt(id));
    return p?.title?.rendered || '';
  };

  const getTaskStatus = (task) => {
    const status   = task.meta?.gpf_status || 'not_started';
    const endDate  = task.meta?.gpf_end_date;
    if (status === 'completed') return 'completed';
    if (endDate && endDate < today && status !== 'completed') return 'overdue';
    return status;
  };

  const filtered = tasks.filter(task => {
    const status = getTaskStatus(task);
    const pri    = task.meta?.gpf_priority || 'medium';
    const title  = decodeEntities(task.title?.rendered)?.toLowerCase() || '';
    const proj   = getProjectName(task.meta?.gpf_project_id)?.toLowerCase() || '';

    if (filter !== 'all' && status !== filter) return false;
    if (priority !== 'all' && pri !== priority) return false;
    if (search && !title.includes(search.toLowerCase()) && !proj.includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all:         tasks.length,
    not_started: tasks.filter(t => getTaskStatus(t) === 'not_started').length,
    in_progress: tasks.filter(t => getTaskStatus(t) === 'in_progress').length,
    overdue:     tasks.filter(t => getTaskStatus(t) === 'overdue').length,
    completed:   tasks.filter(t => getTaskStatus(t) === 'completed').length,
  };

  const statusStyle = (status) => {
    const map = {
      completed:   { bg: '#f0fdf4', text: '#16a34a' },
      overdue:     { bg: '#fef2f2', text: '#dc2626' },
      in_progress: { bg: '#eff6ff', text: '#2563eb' },
      not_started: { bg: '#f8fafc', text: '#64748b' },
    };
    return map[status] || map.not_started;
  };

  const hasAttachments = (task) => {
    try {
      const ids = JSON.parse(task.meta?.gpf_file_ids || '[]');
      return ids.length > 0;
    } catch { return false; }
  };

  const attachmentCount = (task) => {
    try { return JSON.parse(task.meta?.gpf_file_ids || '[]').length; }
    catch { return 0; }
  };

  const goToTask = (task) => {
    const projectId = task.meta?.gpf_project_id;
    if (projectId) navigate(`/projects/${projectId}/tasks?task=${task.id}`);
  };

  const handleSaveTask = async () => {
    setFormError('');
    if (!form.title.trim()) { setFormError('Task name is required.'); return; }
    if (!form.project_id)   { setFormError('Please select a project.'); return; }
    if (!form.start_date)   { setFormError('Start date is required.'); return; }
    if (!form.end_date)     { setFormError('End date is required.'); return; }
    if (form.end_date < form.start_date) { setFormError('End date must be after start date.'); return; }
    setSaving(true);
    try {
      await api.post('/wp/v2/tasks', {
        title:  form.title,
        status: 'publish',
        meta: {
          gpf_project_id: parseInt(form.project_id),
          gpf_priority:   form.priority,
          gpf_status:     form.status,
          gpf_start_date: form.start_date,
          gpf_end_date:   form.end_date,
          gpf_progress:   0,
        }
      });
      setShowForm(false);
      setForm({ title: '', project_id: '', priority: 'medium', start_date: '', end_date: '', status: 'not_started' });
      fetchData();
    } catch(e) { setFormError('Failed to create task. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>My Tasks</h1>
            <p style={styles.pageSubtitle}>{filtered.length} task{filtered.length !== 1 ? 's' : ''} across all projects</p>
          </div>
          <button style={styles.addBtn} onClick={() => { setShowForm(!showForm); setFormError(''); }}>
            {showForm ? '✕ Cancel' : '+ Add Task'}
          </button>
        </div>

        {/* New Task Form */}
        {showForm && (
          <div style={styles.formCard}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>New Task</h3>
            {formError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{formError}</p>}
            <div style={styles.formGrid}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={styles.label}>Task Name *</label>
                <input style={styles.input} placeholder="What needs to be done?" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>
              <div>
                <label style={styles.label}>Project *</label>
                <select style={styles.input} value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})}>
                  <option value="">Select project...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{decodeEntities(p.title?.rendered)}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Priority</label>
                <select style={styles.input} value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Start Date *</label>
                <input style={styles.input} type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
              </div>
              <div>
                <label style={styles.label}>End Date *</label>
                <input style={styles.input} type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
              </div>
              <div>
                <label style={styles.label}>Status</label>
                <select style={styles.input} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSaveTask} disabled={saving}>
                {saving ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={styles.filterBar}>
          {/* Status tabs */}
          <div style={styles.tabs}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                style={{ ...styles.tab, ...(filter === f.key ? styles.tabActive : {}) }}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {counts[f.key] > 0 && (
                  <span style={{ ...styles.tabBadge, background: filter === f.key ? 'rgba(255,255,255,0.3)' : '#e2e8f0' }}>
                    {counts[f.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={styles.rightFilters}>
            {/* Priority filter */}
            <select style={styles.select} value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="all">All Priorities</option>
              <option value="high">🔴 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>

            {/* Search */}
            <input
              style={styles.search}
              placeholder="Search tasks or projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Task List */}
        <div style={styles.list}>
          {loading && <div style={styles.empty}><p>Loading tasks...</p></div>}
          {!loading && filtered.length === 0 && (
            <div style={styles.empty}>
              <p style={styles.emptyTitle}>No tasks found</p>
              <p style={styles.emptySub}>Try adjusting your filters</p>
            </div>
          )}

          {filtered.map(task => {
            const status   = getTaskStatus(task);
            const pri      = task.meta?.gpf_priority || 'medium';
            const priColor = PRIORITY_COLORS[pri] || PRIORITY_COLORS.medium;
            const stColor  = statusStyle(status);
            const projName = getProjectName(task.meta?.gpf_project_id);
            const endDate  = task.meta?.gpf_end_date;
            const progress = parseInt(task.meta?.gpf_progress || 0);
            const attCount = attachmentCount(task);

            const handleSaveTask = async () => {
    setFormError('');
    if (!form.title.trim()) { setFormError('Task name is required.'); return; }
    if (!form.project_id)   { setFormError('Please select a project.'); return; }
    if (!form.start_date)   { setFormError('Start date is required.'); return; }
    if (!form.end_date)     { setFormError('End date is required.'); return; }
    if (form.end_date < form.start_date) { setFormError('End date must be after start date.'); return; }
    setSaving(true);
    try {
      await api.post('/wp/v2/tasks', {
        title:  form.title,
        status: 'publish',
        meta: {
          gpf_project_id: parseInt(form.project_id),
          gpf_priority:   form.priority,
          gpf_status:     form.status,
          gpf_start_date: form.start_date,
          gpf_end_date:   form.end_date,
          gpf_progress:   0,
        }
      });
      setShowForm(false);
      setForm({ title: '', project_id: '', priority: 'medium', start_date: '', end_date: '', status: 'not_started' });
      fetchData();
    } catch(e) { setFormError('Failed to create task. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
              <div
                key={task.id}
                style={styles.taskCard}
                onClick={() => setDetailTask(task)}
              >
                <div style={styles.taskMain}>
                  {/* Left accent */}
                  <div style={{ ...styles.taskAccent, background: status === 'overdue' ? '#dc2626' : status === 'completed' ? '#16a34a' : status === 'in_progress' ? '#2563eb' : '#94a3b8' }} />

                  <div style={styles.taskContent}>
                    {/* Title row */}
                    <div style={styles.taskTitleRow}>
                      <span style={styles.taskTitle}>{decodeEntities(task.title?.rendered) || 'Untitled'}</span>
                      <div style={styles.taskBadges}>
                        {attCount > 0 && (
                          <span style={styles.attachBadge} title={`${attCount} attachment${attCount > 1 ? 's' : ''}`}>
                            📎 {attCount}
                          </span>
                        )}
                        <span style={{ ...styles.badge, background: priColor.bg, color: priColor.text }}>
                          {pri.charAt(0).toUpperCase() + pri.slice(1)}
                        </span>
                        <span style={{ ...styles.badge, background: stColor.bg, color: stColor.text }}>
                          {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                    </div>

                    {/* Meta row */}
                    <div style={styles.taskMeta}>
                      {projName && (
                        <span style={styles.projectTag}>📁 {projName}</span>
                      )}
                      {endDate && (
                        <span style={{ ...styles.dueTag, color: status === 'overdue' ? '#dc2626' : '#64748b' }}>
                          📅 Due {endDate}
                        </span>
                      )}
                    </div>

                    {/* Progress bar */}
                    {progress > 0 && (
                      <div style={styles.progressWrap}>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill, width: `${progress}%`, background: status === 'completed' ? '#16a34a' : '#2563eb' }} />
                        </div>
                        <span style={styles.progressLabel}>{progress}%</span>
                      </div>
                    )}
                  </div>

                  <div style={styles.taskArrow}>→</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={(t) => { setDetailTask(null); const pid = t.meta?.gpf_project_id; if (pid) navigate(`/projects/${pid}/tasks?task=${t.id}&edit=1`); }}
          onOpenNotes={(t) => { setDetailTask(null); goToTask(t); }}
        />
      )}
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 900, margin: '0 auto' },
  addBtn:        { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formCard:      { background: 'white', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
  label:         { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
  input:         { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  cancelBtn:     { padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 },
  saveBtn:       { padding: '9px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle:     { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  pageSubtitle:  { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  filterBar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  tabs:          { display: 'flex', gap: 4, background: '#fff', padding: 4, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  tab:           { padding: '7px 14px', border: 'none', borderRadius: 7, background: 'transparent', fontSize: 13, fontWeight: 500, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  tabActive:     { background: '#2563eb', color: 'white', fontWeight: 600 },
  tabBadge:      { fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 99 },
  rightFilters:  { display: 'flex', gap: 10, alignItems: 'center' },
  select:        { padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
  search:        { padding: '8px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', width: 220 },
  list:          { display: 'flex', flexDirection: 'column', gap: 8 },
  empty:         { textAlign: 'center', padding: '60px 0', background: 'white', borderRadius: 12 },
  emptyTitle:    { fontSize: 16, fontWeight: 600, color: '#374151', margin: 0 },
  emptySub:      { fontSize: 14, color: '#94a3b8', margin: '6px 0 0' },
  taskCard:      { background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'box-shadow 0.15s', ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } },
  taskMain:      { display: 'flex', alignItems: 'stretch' },
  taskAccent:    { width: 4, flexShrink: 0 },
  taskContent:   { flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 },
  taskTitleRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  taskTitle:     { fontSize: 15, fontWeight: 600, color: '#0f172a', lineHeight: 1.4 },
  taskBadges:    { display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' },
  badge:         { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 },
  attachBadge:   { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#fef3c7', color: '#d97706' },
  taskMeta:      { display: 'flex', gap: 16, flexWrap: 'wrap' },
  projectTag:    { fontSize: 12, color: '#64748b' },
  dueTag:        { fontSize: 12 },
  progressWrap:  { display: 'flex', alignItems: 'center', gap: 8 },
  progressBar:   { flex: 1, height: 4, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  progressFill:  { height: '100%', borderRadius: 99, transition: 'width 0.3s' },
  progressLabel: { fontSize: 11, color: '#64748b', minWidth: 28 },
  taskArrow:     { display: 'flex', alignItems: 'center', padding: '0 16px', color: '#cbd5e1', fontSize: 18 },
};